import { Component, OnInit, OnDestroy } from '@angular/core';
import { AuthenticationService } from '../../../services/authentication.service';
import { HelpersService } from '../../../services/helpers.service';
import { UserService } from '../../../services/user.service';
import { PartnerService } from '../../../services/partner.service';
import { Router, ActivatedRoute } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, EventMessage, EventType, AuthenticationResult } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AppConfig } from 'src/app/app.config';

@Component({
  selector: 'app-entra-login',
  templateUrl: './entra-login.component.html',
  styleUrls: ['./entra-login.component.scss']
})
export class EntraLoginComponent implements OnInit, OnDestroy {

  public loggingIn: boolean = false;
  public error: boolean = false;
  public errorMessage: string = '';
  public returnURL: string = null;
  public msalInitialized = false;
  public isAuthInProgress = false;
  public isCallbackRoute = false;
  private entraConfig: any;

  private readonly _destroying$ = new Subject<void>();

  constructor(
    public authenticationService: AuthenticationService,
    public userService: UserService,
    public partnerService: PartnerService,
    public helpersService: HelpersService,
    public router: Router,
    public route: ActivatedRoute,
    private msal: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private appConfig: AppConfig
  ) { 
    this.entraConfig = this.appConfig.getEntraConfig();
  }


  async ngOnInit() {
    // Get return URL from query params (if it exists)
    this.route.queryParams.subscribe(params => {
      this.returnURL = params['returnUrl'];
    });

    // Parse the hash fragment for OAuth response
    const hashParams = this.parseHashFragment(window.location.hash);

    // Check if we have an authorization code from redirect
    const code = hashParams['code'];
    const state = hashParams['state'];

    if (code) {
      console.log('Authorization code received from hash:', code);
      console.log('State:', state);

      // Get the code_verifier we stored BEFORE the redirect
      const codeVerifier = sessionStorage.getItem('pkce_code_verifier');

      if (codeVerifier) {
        console.log('Code verifier found in storage');

        // Clean up the URL (remove hash)
        window.history.replaceState({}, document.title, window.location.pathname);

        this.handleAuthorizationCode(code, codeVerifier);

        // Clear the verifier after use
        sessionStorage.removeItem('pkce_code_verifier');
        sessionStorage.removeItem('pkce_state');
      } else {
        console.error('Code verifier not found in storage');
        this.error = true;
        this.errorMessage = 'Authentication state not found. Please try again.';
      }
      return;
    }

    await this.initializeMsal();

  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }


  async initializeMsal() {
    try {
      await this.msal.instance.initialize();
      this.msalInitialized = true;
      console.log('MSAL initialized successfully');

      // Check if user is already logged in
      const accounts = this.msal.instance.getAllAccounts();
      if (accounts.length > 0) {
        console.log('User already logged in:', accounts[0]);
        this.msal.instance.setActiveAccount(accounts[0]);
      }
    } catch (error) {
      console.error('MSAL initialization error:', error);
      this.msalInitialized = true;
    }

    // Monitor interaction status
    this.msalBroadcastService.inProgress$
      .pipe(takeUntil(this._destroying$))
      .subscribe((status: InteractionStatus) => {
        console.log('Interaction status:', status);
        this.isAuthInProgress =
          status === InteractionStatus.Login ||
          status === InteractionStatus.AcquireToken;
      });
  }

  /**
   * Generate a cryptographically random string for PKCE
   */
  private generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);
    let result = '';
    for (let i = 0; i < length; i++) {
      result += charset[randomValues[i] % charset.length];
    }
    return result;
  }

  /**
   * Generate SHA256 hash and base64url encode it
   */
  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const hash = await crypto.subtle.digest('SHA-256', data);

    // Convert to base64url
    return this.base64UrlEncode(new Uint8Array(hash));
  }

  /**
   * Base64URL encode
   */
  private base64UrlEncode(buffer: Uint8Array): string {
    const base64 = btoa(String.fromCharCode(...buffer));
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  async loginToEntra() {
    if (this.isAuthInProgress) {
      console.log('Authentication already in progress, please wait...');
      return;
    }

    this.loggingIn = true;
    this.error = false;
    this.errorMessage = '';

    try {
      // Generate PKCE parameters
      const codeVerifier = this.generateRandomString(128);
      const codeChallenge = await this.generateCodeChallenge(codeVerifier);
      const state = this.generateRandomString(32);
      const nonce = this.generateRandomString(32);

      // Store code_verifier and state in sessionStorage
      sessionStorage.setItem('pkce_code_verifier', codeVerifier);
      sessionStorage.setItem('pkce_state', state);

      // Build authorization URL manually
      const redirectUri = window.location.origin + window.location.pathname;
      const scope = 'openid profile email';

      const authUrl = `${this.entraConfig.authority}/oauth2/v2.0/authorize?` +
        `client_id=${this.entraConfig.clientId}` +
        `&response_type=code` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&response_mode=fragment` +
        `&scope=${encodeURIComponent(scope)}` +
        `&state=${state}` +
        `&nonce=${nonce}` +
        `&code_challenge=${codeChallenge}` +
        `&code_challenge_method=S256` +
        `&prompt=select_account`;

      console.log('Redirecting to:', authUrl);

      // Redirect to Azure AD
      window.location.href = authUrl;

    } catch (error) {
      console.error('Entra login failed:', error);
      this.loggingIn = false;
      this.error = true;
      this.errorMessage = 'SSO login failed. Please try again.';
    }
  }

  private handleAuthorizationCode(code: string, codeVerifier: string) {
    this.loggingIn = true;

    console.log('Sending to backend:');
    console.log('  - Authorization code (first 20 chars):', code.substring(0, 20));
    console.log('  - Code verifier (first 20 chars):', codeVerifier.substring(0, 20));

    // Send the authorization code AND code_verifier to your backend
    this.authenticationService.authenticateWithEntraCode(code, codeVerifier, window.location.origin)
      .subscribe(
        (authResult) => {
          // Backend returns your standard auth tokens
          this.handleEntraLoginSuccess(authResult);
        },
        (error) => {
          console.error('Backend authentication failed:', error);
          this.loggingIn = false;
          this.error = true;
          this.errorMessage = error?.error?.message || 'Microsoft authentication failed. Please try again.';
        }
      );
  }

  private async handleEntraLoginSuccess(authResult: any) {
    try {
      this.loggingIn = true;

      // Save tokens from your backend
      this.helpersService.saveAuthData(authResult);
      this.helpersService.saveAuthDataNew(authResult);

      // Get user profile
      this.userService.getUserDetail(authResult.userName).subscribe(
        async (user) => {
          const profile = user;
          const roles = profile.UserRoles.filter((x) => x.PartnerID > 0);

          var shops = roles
            .map((x) => x.PartnerID)
            .filter((value, index, self) => self.indexOf(value) === index);

          if (shops.length > 0) {
            var defaultPartnerID = shops[0];
            profile.PartnerID = defaultPartnerID;

            var shopRoles = roles
              .filter((x) => x.PartnerID === defaultPartnerID)
              .map((x) => x.RoleID);

            profile.isAdmin = shopRoles.includes(2) || profile.UserRoles.includes(3);
            profile.isManager = shopRoles.includes(3);
            profile.isTechnician =
              (shopRoles.includes(1) || shopRoles.includes(4)) &&
              !profile.isAdmin &&
              !profile.isManager;
          }

          this.helpersService.saveProfileData(profile);

          this.userService.getUserRolesInsurance(profile.ID).subscribe(
            (permissions) => {
              this.helpersService.saveProfileInsuranceRoles(permissions);
            },
            (error) => {
              console.error('Error loading insurance roles:', error);
            }
          );

          // Navigate based on user role
          if (this.returnURL) {
            this.router.navigateByUrl(this.returnURL);
          } else if (profile.IsSuperUser) {
            this.router.navigateByUrl('/administration/stores');
          } else if (profile.isAdmin || profile.isManager || profile.isTechnician) {
            this.router.navigateByUrl('/store/product-redirect-loader');
          } else {
            this.error = true;
            this.errorMessage = 'User does not have a valid role assigned to any store.';
            this.isAuthInProgress = false;
            await this.initializeMsal();
          }

          this.loggingIn = false;
        },
        (error) => {
          console.error('Error loading user profile:', error);
          this.loggingIn = false;
          this.error = true;
          this.errorMessage = 'Failed to load user profile. Please try again.';
        }
      );
    } catch (error) {
      console.error('Error handling Entra login:', error);
      this.loggingIn = false;
      this.error = true;
      this.errorMessage = 'Authentication failed. Please try again.';
    }
  }

  private parseHashFragment(hash: string): { [key: string]: string } {
    const params: { [key: string]: string } = {};

    if (!hash || hash.length === 0) {
      return params;
    }

    // Remove the leading '#' if present
    const hashContent = hash.startsWith('#') ? hash.substring(1) : hash;

    // Split by '&' to get individual parameters
    const pairs = hashContent.split('&');

    for (const pair of pairs) {
      const [key, value] = pair.split('=');
      if (key && value) {
        params[key] = decodeURIComponent(value);
      }
    }

    return params;
  }
}